import { useEffect, useMemo, useState, type MouseEvent } from "react";
import { Menu, X } from "lucide-react";
import { useLocation } from "wouter";
import { useEnvironment } from "@/hooks/useEnvironment";
import { storeBookingSelection } from "@/lib/bookingSelection";
import { CANONICAL_ROUTES, withTrailingSlash } from "@/lib/utils";

interface NavigationBannerProps {
  currentPage: "home" | "chi-sono" | "blog" | "prenota" | "testimonianze";
}

export default function NavigationBanner({ currentPage }: NavigationBannerProps) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState<"home" | "counselling" | "servizi">("home");
  const [pathname, setLocation] = useLocation();
  const canonicalPathname = withTrailingSlash(pathname);
  const { isTest } = useEnvironment();

  const shouldHandleClientNavigation = (event: MouseEvent<HTMLAnchorElement>) => {
    if (event.defaultPrevented) {
      return false;
    }

    return event.button === 0 && !event.metaKey && !event.altKey && !event.ctrlKey && !event.shiftKey;
  };

  const handleAnchorNavigation = (event: MouseEvent<HTMLAnchorElement>, callback: () => void) => {
    if (!shouldHandleClientNavigation(event)) {
      return;
    }

    event.preventDefault();
    callback();
  };

  useEffect(() => {
    if (currentPage !== "home") {
      setActiveSection("home");
      return;
    }

    const sectionIds: Array<"home" | "counselling" | "servizi"> = ["home", "counselling", "servizi"];
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];

        if (visible) {
          const id = visible.target.id as typeof sectionIds[number];
          setActiveSection(id);
        }
      },
      {
        root: null,
        rootMargin: "-45% 0px -45% 0px",
        threshold: 0,
      }
    );

    sectionIds.forEach((id) => {
      const element = document.getElementById(id);
      if (element) {
        observer.observe(element);
      }
    });

    return () => observer.disconnect();
  }, [currentPage]);

  const desktopButtonClass = useMemo(
    () =>
      ({ key }: { key: "home" | "counselling" | "servizi" | "chi-sono" | "blog" | "testimonianze" | "prenota" }) => {
        const isActive = currentPage === "home"
          ? key === "home" || key === "counselling" || key === "servizi"
            ? activeSection === key
            : false
          : currentPage === key;

        return `text-sm tracking-wide font-medium transition-colors ${
          isActive ? "text-primary" : "text-foreground hover:text-primary"
        }`;
      },
    [activeSection, currentPage]
  );

  const mobileButtonClass = desktopButtonClass;

  const homeHrefForSection = (id: "home" | "counselling" | "servizi") =>
    id === "home" ? CANONICAL_ROUTES.home : `${CANONICAL_ROUTES.home}#${id}`;

  const scrollOrNavigate = (target: string) => {
    if (canonicalPathname === target) {
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      setLocation(target);
    }
    setMobileMenuOpen(false);
  };

  const scrollToSection = (id: string) => {
    // Se non siamo nella home, naviga prima alla home
    if (currentPage !== "home") {
      setLocation(CANONICAL_ROUTES.home);
      // Aspetta che la navigazione completi, poi scrolla
      setTimeout(() => {
        if (id === "home") {
          window.scrollTo({ top: 0, behavior: "smooth" });
        } else {
          const element = document.getElementById(id);
          if (element) {
            const navHeight = 96; // navbar height (6rem)
            const elementTop = element.getBoundingClientRect().top + window.scrollY;
            const targetScroll = elementTop - navHeight;
            window.scrollTo({ top: targetScroll, behavior: "smooth" });
          }
        }
      }, 100);
    } else {
      // Se siamo già nella home, scrolla direttamente
      if (id === "home") {
        window.scrollTo({ top: 0, behavior: "smooth" });
      } else {
        const element = document.getElementById(id);
        if (element) {
          const navHeight = 96; // navbar height (6rem)
          const elementTop = element.getBoundingClientRect().top + window.scrollY;
          const targetScroll = elementTop - navHeight;
          window.scrollTo({ top: targetScroll, behavior: "smooth" });
        }
      }
    }
    setMobileMenuOpen(false);
  };

  const handlePrenotaNavigation = () => {
    storeBookingSelection({ type: "online", lock: false });
    scrollOrNavigate(CANONICAL_ROUTES.prenota);
  };

  const handleTestimonianzeNavigation = () => {
    scrollOrNavigate(CANONICAL_ROUTES.testimonianze);
  };

  return (
    <nav
      className={`fixed top-0 w-full backdrop-blur-md border-b z-50 shadow-sm ${
        isTest ? "bg-yellow-300/95 border-yellow-400" : "bg-background/95 border-border"
      }`}
    >
      <div className="container mx-auto px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img 
              src="/aura-logo-no-stripes.png" 
              alt="Aura Counselling" 
              width={120}
              height={120}
              className="w-[120px] h-auto" 
            />
          </div>
          
          {/* Desktop Menu */}
          <div className="hidden md:flex items-center gap-8">
            <a
              href={homeHrefForSection("home")}
              onClick={(event) => handleAnchorNavigation(event, () => scrollToSection("home"))}
              className={desktopButtonClass({ key: "home" })}
            >
              Home
            </a>
            <a
              href={homeHrefForSection("counselling")}
              onClick={(event) => handleAnchorNavigation(event, () => scrollToSection("counselling"))}
              className={desktopButtonClass({ key: "counselling" })}
            >
              Counselling
            </a>
            <a
              href={homeHrefForSection("servizi")}
              onClick={(event) => handleAnchorNavigation(event, () => scrollToSection("servizi"))}
              className={desktopButtonClass({ key: "servizi" })}
            >
              Servizi
            </a>
            <a
              href={CANONICAL_ROUTES.chiSono}
              onClick={(event) =>
                handleAnchorNavigation(event, () => scrollOrNavigate(CANONICAL_ROUTES.chiSono))
              }
              className={desktopButtonClass({ key: "chi-sono" })}
            >
              Chi Sono
            </a>
            <a
              href={CANONICAL_ROUTES.blog}
              onClick={(event) =>
                handleAnchorNavigation(event, () => scrollOrNavigate(CANONICAL_ROUTES.blog))
              }
              className={desktopButtonClass({ key: "blog" })}
            >
              Blog
            </a>
            <a
              href={CANONICAL_ROUTES.testimonianze}
              onClick={(event) => handleAnchorNavigation(event, handleTestimonianzeNavigation)}
              className={desktopButtonClass({ key: "testimonianze" })}
            >
              Testimonianze
            </a>
            <a
              href={CANONICAL_ROUTES.prenota}
              onClick={(event) => handleAnchorNavigation(event, handlePrenotaNavigation)}
              className={desktopButtonClass({ key: "prenota" })}
            >
              Prenotazione
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="md:hidden">
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden mt-4 pb-4 space-y-3">
            <a
              href={homeHrefForSection("home")}
              onClick={(event) => handleAnchorNavigation(event, () => scrollToSection("home"))}
              className={`${mobileButtonClass({ key: "home" })} block w-full text-left py-2`}
            >
              Home
            </a>
            <a
              href={homeHrefForSection("counselling")}
              onClick={(event) => handleAnchorNavigation(event, () => scrollToSection("counselling"))}
              className={`${mobileButtonClass({ key: "counselling" })} block w-full text-left py-2`}
            >
              Counselling
            </a>
            <a
              href={homeHrefForSection("servizi")}
              onClick={(event) => handleAnchorNavigation(event, () => scrollToSection("servizi"))}
              className={`${mobileButtonClass({ key: "servizi" })} block w-full text-left py-2`}
            >
              Servizi
            </a>
            <a
              href={CANONICAL_ROUTES.chiSono}
              onClick={(event) =>
                handleAnchorNavigation(event, () => scrollOrNavigate(CANONICAL_ROUTES.chiSono))
              }
              className={`${mobileButtonClass({ key: "chi-sono" })} block w-full text-left py-2`}
            >
              Chi Sono
            </a>
            <a
              href={CANONICAL_ROUTES.blog}
              onClick={(event) =>
                handleAnchorNavigation(event, () => scrollOrNavigate(CANONICAL_ROUTES.blog))
              }
              className={`${mobileButtonClass({ key: "blog" })} block w-full text-left py-2`}
            >
              Blog
            </a>
            <a
              href={CANONICAL_ROUTES.testimonianze}
              onClick={(event) => handleAnchorNavigation(event, handleTestimonianzeNavigation)}
              className={`${mobileButtonClass({ key: "testimonianze" })} block w-full text-left py-2`}
            >
              Testimonianze
            </a>
            <a
              href={CANONICAL_ROUTES.prenota}
              onClick={(event) => handleAnchorNavigation(event, handlePrenotaNavigation)}
              className={`${mobileButtonClass({ key: "prenota" })} block w-full text-left py-2`}
            >
              Prenotazione
            </a>
          </div>
        )}
      </div>
    </nav>
  );
}
